import { useCallback, useEffect, useRef, useState } from 'react';
import { useDaily, useDailyEvent } from '@daily-co/daily-react';
import { useConversaStore } from '@/stores/conversaStore';
import { useAuthStore } from '@/stores/authStore';
import { notifications } from '@mantine/notifications';

interface UseTranscriptionProps {
  roomCode: string;
  isOwner: boolean;
  onSegmentComplete?: (text: string) => void;
}

interface TranscriptBuffer {
  participantId: string;
  participantName: string;
  textParts: string[];
  startTime: number;
}

// Configurações de transcrição em ordem de preferência (melhor → básica)
const TRANSCRIPTION_CONFIGS = [
  {
    level: 1,
    name: 'Nova-3 com Smart Format',
    config: { model: 'nova-3', language: 'pt-BR', smart_format: true }
  },
  {
    level: 2,
    name: 'Padrão com Smart Format',
    config: { language: 'pt-BR', smart_format: true }
  },
  {
    level: 3,
    name: 'Básico',
    config: { language: 'pt-BR' }
  },
];

const STORAGE_KEY = 'daily_transcription_config_level';

export const useTranscription = ({ roomCode, isOwner, onSegmentComplete }: UseTranscriptionProps) => {
  const callObject = useDaily();
  const addTranscriptionMessage = useConversaStore((state) => state.addTranscriptionMessage);
  const user = useAuthStore((state) => state.user);

  // Local state for transcription status (replacing transcriptStore)
  const [isTranscribing, setIsTranscribing] = useState(false);

  // Buffer para acumular transcrições antes de exibir/salvar
  const bufferRef = useRef<Map<string, TranscriptBuffer>>(new Map());
  const timeoutRef = useRef<Map<string, NodeJS.Timeout>>(new Map());

  // Controle de fallback
  const currentConfigLevelRef = useRef<number>(1);
  const isRetryingRef = useRef<boolean>(false);

  // Função para processar e salvar buffer acumulado
  const flushBuffer = useCallback(
    async (participantId: string) => {
      const buffer = bufferRef.current.get(participantId);
      if (!buffer || buffer.textParts.length === 0) return;

      const fullText = buffer.textParts.join(' ').trim();
      const wordCount = fullText.split(/\s+/).length;

      console.log(`📊 Flushing buffer for ${buffer.participantName}: ${wordCount} words`);

      // Add to unified Conversa store (handles both display and persistence)
      // The conversaStore.addTranscriptionMessage will save to backend via conversationsApi
      addTranscriptionMessage(buffer.participantName, fullText);

      // Call onSegmentComplete callback for agent integration
      if (onSegmentComplete) {
        onSegmentComplete(fullText);
      }

      // Limpar buffer
      bufferRef.current.delete(participantId);

      // Limpar timeout se existir
      const timeout = timeoutRef.current.get(participantId);
      if (timeout) {
        clearTimeout(timeout);
        timeoutRef.current.delete(participantId);
      }
    },
    [addTranscriptionMessage, onSegmentComplete]
  );

  // Listen for transcription started
  useDailyEvent(
    'transcription-started',
    useCallback(() => {
      console.log('✅ Transcription started');
      setIsTranscribing(true);
      isRetryingRef.current = false;

      // Salvar configuração que funcionou
      const successLevel = currentConfigLevelRef.current;
      localStorage.setItem(STORAGE_KEY, successLevel.toString());

      const configUsed = TRANSCRIPTION_CONFIGS[successLevel - 1];
      console.log(`✅ Transcription config level ${successLevel} working: ${configUsed.name}`);

      // Notificação diferenciada baseada no nível usado
      if (successLevel === 1) {
        notifications.show({
          title: 'Transcrição iniciada',
          message: '🚀 Usando modelo avançado Nova-3 com formatação inteligente',
          color: 'green',
        });
      } else if (successLevel === 2) {
        notifications.show({
          title: 'Transcrição iniciada',
          message: '✨ Usando formatação inteligente',
          color: 'blue',
        });
      } else {
        notifications.show({
          title: 'Transcrição iniciada',
          message: 'As falas estão sendo transcritas em tempo real',
          color: 'green',
        });
      }
    }, [])
  );

  // Listen for transcription stopped
  useDailyEvent(
    'transcription-stopped',
    useCallback(() => {
      console.log('⏹️ Transcription stopped');
      setIsTranscribing(false);

      // Flush todos os buffers pendentes
      const participantIds = Array.from(bufferRef.current.keys());
      participantIds.forEach((id) => {
        flushBuffer(id);
      });

      // Limpar todos os timeouts
      timeoutRef.current.forEach((timeout) => clearTimeout(timeout));
      timeoutRef.current.clear();

      notifications.show({
        title: 'Transcrição parada',
        message: 'A transcrição foi finalizada',
        color: 'blue',
      });
    }, [flushBuffer])
  );

  // Listen for transcription errors
  useDailyEvent(
    'transcription-error',
    useCallback(
      async (event: any) => {
        console.error('❌ Transcription error event:', event);
        console.error('❌ Error details:', {
          error: event?.error,
          errorMsg: event?.errorMsg,
          msg: event?.msg,
        });

        const errorMessage = event?.errorMsg || event?.error?.msg || event?.msg || 'Ocorreu um erro ao transcrever';

        // Se for erro 403 e ainda não estamos retrying, tentar fallback
        if (errorMessage.includes('403') && !isRetryingRef.current) {
          console.log(`⚠️ Config level ${currentConfigLevelRef.current} failed with 403, trying fallback...`);

          // Tentar próximo nível
          const nextLevel = currentConfigLevelRef.current + 1;

          if (nextLevel <= TRANSCRIPTION_CONFIGS.length) {
            isRetryingRef.current = true;
            currentConfigLevelRef.current = nextLevel;

            const configToTry = TRANSCRIPTION_CONFIGS[nextLevel - 1];
            console.log(`🔄 Retrying with level ${nextLevel}: ${configToTry.name}`);

            notifications.show({
              title: 'Tentando configuração alternativa...',
              message: `Usando: ${configToTry.name}`,
              color: 'blue',
              autoClose: 3000,
            });

            // Aguarda um pouco antes de tentar novamente
            setTimeout(() => {
              if (callObject && isOwner) {
                try {
                  console.log('🎙️ Starting transcription with fallback config:', configToTry.config);
                  callObject.startTranscription(configToTry.config);
                } catch (error) {
                  console.error('Error on retry:', error);
                  isRetryingRef.current = false;
                }
              }
            }, 1000);

            return; // Não mostra erro ainda, vai tentar fallback
          } else {
            // Esgotou todas as opções
            setIsTranscribing(false);
            isRetryingRef.current = false;
            notifications.show({
              title: 'Transcrição não disponível',
              message: 'Sua conta Daily.co não suporta transcrição. Verifique o painel da Daily.',
              color: 'red',
              autoClose: 10000,
            });
          }
        } else if (!errorMessage.includes('403')) {
          // Erro que não é 403
          setIsTranscribing(false);
          notifications.show({
            title: 'Erro na transcrição',
            message: errorMessage,
            color: 'red',
          });
        }
      },
      [callObject, isOwner]
    )
  );

  // Listen for transcription messages
  useDailyEvent(
    'transcription-message',
    useCallback(
      async (event: any) => {
        console.log('🎤 Raw transcription event:', event);

        if (!event) {
          console.warn('⚠️ Transcription event is null/undefined');
          return;
        }

        // Daily.co sends transcription data in different formats
        const transcriptionData = event.transcription || event.data || event;
        console.log('📋 Transcription data:', transcriptionData);

        if (!transcriptionData) {
          console.warn('⚠️ No transcription data found in event');
          return;
        }

        const participantId = transcriptionData.participantId || transcriptionData.participant_id;
        const text = transcriptionData.text || transcriptionData.rawTranscript;
        const isFinal = transcriptionData.is_final || transcriptionData.isFinal || false;

        if (!text || !text.trim()) {
          return; // Ignora transcrições vazias
        }

        // Get participant name com tratamento especial para o participante local (owner)
        const participants = callObject?.participants();
        let participantName = 'Desconhecido';

        // Primeira tentativa: buscar pelo ID do participante
        if (participantId && participants?.[participantId]) {
          participantName = participants[participantId].user_name || participantName;
        }
        // Segunda tentativa: verificar se é o participante local
        else if (participants?.local) {
          const localParticipant = participants.local;
          // Verificar se este evento é do participante local comparando IDs ou se não tem ID
          if (!participantId || localParticipant.session_id === participantId || localParticipant.user_id === participantId) {
            participantName = localParticipant.user_name || participantName;
          }
        }

        // Terceira tentativa: se é owner e ainda não resolveu, usar nome do usuário logado
        if (participantName === 'Desconhecido' && isOwner && user?.name) {
          participantName = user.name;
        }

        console.log(`📝 Transcript part from ${participantName}: "${text}" (final: ${isFinal})`);

        // Obter ou criar buffer para este participante
        let buffer = bufferRef.current.get(participantId);
        if (!buffer) {
          buffer = {
            participantId,
            participantName,
            textParts: [],
            startTime: Date.now(),
          };
          bufferRef.current.set(participantId, buffer);
        }

        // Adicionar texto ao buffer
        buffer.textParts.push(text);

        // Contar palavras acumuladas
        const fullText = buffer.textParts.join(' ');
        const wordCount = fullText.split(/\s+/).filter((w) => w.length > 0).length;

        console.log(`📊 Buffer status for ${participantName}: ${wordCount} words, ${buffer.textParts.length} parts`);

        // Verificar condições para flush:
        // 1. Mínimo de 10 palavras OU
        // 2. Passou 3 segundos desde o início do buffer OU
        // 3. É uma transcrição final (is_final)
        const elapsed = Date.now() - buffer.startTime;
        const shouldFlush = wordCount >= 10 || elapsed >= 3000 || isFinal;

        if (shouldFlush) {
          console.log(`✅ Flushing: ${wordCount} words, ${elapsed}ms elapsed, isFinal: ${isFinal}`);
          await flushBuffer(participantId);
        } else {
          // Agendar flush para 3 segundos se ainda não agendou
          if (!timeoutRef.current.has(participantId)) {
            const timeout = setTimeout(() => {
              console.log('⏰ Timeout triggered for', participantName);
              flushBuffer(participantId);
            }, 3000);
            timeoutRef.current.set(participantId, timeout);
          }
        }
      },
      [callObject, flushBuffer, user, isOwner]
    )
  );

  // Start transcription
  const startTranscription = useCallback(() => {
    if (!callObject || !isOwner) {
      notifications.show({
        title: 'Permissão negada',
        message: 'Apenas o criador da sala pode iniciar a transcrição',
        color: 'red',
      });
      return;
    }

    // Debug: Check current participant permissions
    const localParticipant = callObject.participants()?.local;
    console.log('🔍 Local participant info:', {
      user_name: localParticipant?.user_name,
      is_owner: localParticipant?.owner,
      permissions: localParticipant?.permissions,
    });

    try {
      // Resetar flags
      isRetryingRef.current = false;

      // Tentar usar configuração salva anteriormente, ou começar do nível 1
      const savedLevel = localStorage.getItem(STORAGE_KEY);
      const startLevel = savedLevel ? parseInt(savedLevel) : 1;

      currentConfigLevelRef.current = startLevel;
      const configToUse = TRANSCRIPTION_CONFIGS[startLevel - 1];

      console.log(`🎙️ Starting transcription with level ${startLevel}: ${configToUse.name}`);
      console.log('📋 Config:', configToUse.config);

      callObject.startTranscription(configToUse.config);
      console.log('✅ Transcription start command sent');
    } catch (error) {
      console.error('Error starting transcription:', error);
      notifications.show({
        title: 'Erro ao iniciar transcrição',
        message: 'Não foi possível iniciar a transcrição',
        color: 'red',
      });
    }
  }, [callObject, isOwner]);

  // Stop transcription
  const stopTranscription = useCallback(() => {
    if (!callObject || !isOwner) {
      notifications.show({
        title: 'Permissão negada',
        message: 'Apenas o criador da sala pode parar a transcrição',
        color: 'red',
      });
      return;
    }

    try {
      callObject.stopTranscription();
      console.log('⏹️ Stopping transcription...');
    } catch (error) {
      console.error('Error stopping transcription:', error);
      notifications.show({
        title: 'Erro ao parar transcrição',
        message: 'Não foi possível parar a transcrição',
        color: 'red',
      });
    }
  }, [callObject, isOwner]);

  // Cleanup timeouts on unmount
  useEffect(() => {
    return () => {
      // Clear all pending timeouts
      timeoutRef.current.forEach((timeout) => clearTimeout(timeout));
      timeoutRef.current.clear();
    };
  }, []);

  return {
    isTranscribing,
    startTranscription,
    stopTranscription,
  };
};
